/**
 * Help & Support Analytics Utility
 * Privacy-safe engagement tracking for the Help & Support page
 * 
 * Events tracked:
 * - help_view: User viewed the Help & Support page
 * - faq_open: User opened a specific FAQ
 * - contact_click: User clicked a contact method
 * - copy_click: User copied contact info
 * - search_help: User searched help content (query length only, NOT the query)
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or retrieve session ID for anonymous tracking
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('help_analytics_session');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem('help_analytics_session', sessionId);
  }
  return sessionId;
};

// Check if user has opted out of analytics
const isAnalyticsEnabled = () => {
  // Check localStorage for opt-out preference (synced from user settings)
  const optOut = localStorage.getItem('analytics_opt_out');
  return optOut !== 'true';
};

/**
 * Track a Help & Support analytics event
 * @param {string} eventName - One of: help_view, faq_open, contact_click, copy_click, search_help
 * @param {Object} payload - Event-specific data (will be sanitized server-side)
 */
export const trackHelpEvent = async (eventName, payload = {}) => {
  // Skip if user opted out
  if (!isAnalyticsEnabled()) {
    return;
  }

  try {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/analytics/help-event`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        event_name: eventName,
        payload,
        session_id: getSessionId(),
      }),
    });

    // Silently handle errors - analytics should never break the UX
    if (!response.ok) {
      console.debug('[HelpAnalytics] Event tracking failed silently');
    }
  } catch (error) {
    // Silently fail - analytics errors should not affect user experience
    console.debug('[HelpAnalytics] Event tracking error:', error.message);
  }
};

/**
 * Track page view for Help & Support
 */
export const trackHelpPageView = () => {
  trackHelpEvent('help_view');
};

/**
 * Track FAQ item being opened
 * @param {string} faqId - Identifier of the FAQ item
 */
export const trackFaqOpen = (faqId) => {
  trackHelpEvent('faq_open', { faq_id: faqId });
};

/**
 * Track contact method click
 * @param {string} contactType - 'email' or 'whatsapp'
 */
export const trackContactClick = (contactType) => {
  trackHelpEvent('contact_click', { contact_type: contactType });
};

/**
 * Track copy action for contact info
 * @param {string} contactType - 'email' or 'whatsapp'
 */
export const trackCopyClick = (contactType) => {
  trackHelpEvent('copy_click', { contact_type: contactType });
};

/**
 * Track help search (privacy-safe - only tracks query length, not content)
 * @param {string} query - The search query (only length will be tracked)
 */
export const trackHelpSearch = (query) => {
  trackHelpEvent('search_help', { query_length: query?.length || 0 });
};

/**
 * Update analytics opt-out preference on server
 * @param {boolean} optOut - true to disable analytics, false to enable
 * @returns {Promise<boolean>} success status
 */
export const updateAnalyticsPreference = async (optOut) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      // For non-logged-in users, just use localStorage
      localStorage.setItem('analytics_opt_out', optOut ? 'true' : 'false');
      return true;
    }

    const response = await fetch(`${API_URL}/api/users/me/analytics-preference?opt_out=${optOut}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.ok) {
      // Sync to localStorage for quick checks
      localStorage.setItem('analytics_opt_out', optOut ? 'true' : 'false');
      return true;
    }
    return false;
  } catch (error) {
    console.error('[HelpAnalytics] Failed to update preference:', error);
    return false;
  }
};

/**
 * Get current analytics opt-out preference
 * @returns {Promise<boolean>} true if opted out
 */
export const getAnalyticsPreference = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return localStorage.getItem('analytics_opt_out') === 'true';
    }

    const response = await fetch(`${API_URL}/api/users/me/analytics-preference`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      // Sync to localStorage
      localStorage.setItem('analytics_opt_out', data.analytics_opt_out ? 'true' : 'false');
      return data.analytics_opt_out;
    }
    
    return localStorage.getItem('analytics_opt_out') === 'true';
  } catch (error) {
    return localStorage.getItem('analytics_opt_out') === 'true';
  }
};

export default {
  trackHelpEvent,
  trackHelpPageView,
  trackFaqOpen,
  trackContactClick,
  trackCopyClick,
  trackHelpSearch,
  updateAnalyticsPreference,
  getAnalyticsPreference,
};
